import { useState } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { SITE_URL } from "@/lib/siteConfig";
import {
  Zap, Clock, GitBranch, Mail, ArrowRight, ArrowDown,
  Check, ShoppingCart, Monitor, Building2, Sparkles,
  MessageSquare, Download, Play, Target, Globe,
} from "lucide-react";

const AUTOMATIONS = [
  { slug: "auto-welcome-series", name: "Welcome Series", desc: "Greet new subscribers with a 3-email nurture sequence.", trigger: "New Subscriber", steps: 7, category: "onboarding" },
  { slug: "auto-abandoned-cart", name: "Abandoned Cart Recovery", desc: "Recover lost sales with timed reminders + discount.", trigger: "Tag: abandoned_cart", steps: 8, category: "recovery" },
  { slug: "auto-re-engagement", name: "Re-engagement Flow", desc: "Win back cold subscribers before they churn.", trigger: "30 days inactive", steps: 5, category: "re-engagement" },
  { slug: "auto-post-purchase", name: "Post-Purchase Thank You", desc: "Thank buyers, share tips, and ask for reviews.", trigger: "Tag: purchased", steps: 7, category: "retention" },
  { slug: "auto-lead-nurture", name: "Lead Nurture Sequence", desc: "Educate and convert leads over 2 weeks.", trigger: "Tag: lead", steps: 9, category: "nurture" },
  { slug: "auto-saas-onboarding", name: "SaaS Trial Onboarding", desc: "Guide trial users to activation and upgrade.", trigger: "New Subscriber", steps: 9, category: "onboarding" },
  { slug: "auto-birthday", name: "Birthday / Anniversary", desc: "Send personalized celebration emails + offers.", trigger: "Webhook", steps: 4, category: "engagement" },
  { slug: "auto-referral-request", name: "Referral Request", desc: "Ask happy customers for referrals at the right time.", trigger: "Tag: happy_customer", steps: 3, category: "growth" },
  { slug: "auto-engagement-drop", name: "Engagement Drop Alert", desc: "Re-engage when engagement score drops to cold.", trigger: "Tag: engagement_cold", steps: 6, category: "retention" },
  { slug: "auto-reply-followup", name: "Reply Follow-Up", desc: "Auto-follow-up when a subscriber replies.", trigger: "Reply Received", steps: 4, category: "outreach" },
  { slug: "auto-vip-upgrade", name: "VIP Customer Recognition", desc: "Reward your best customers with exclusive perks.", trigger: "Tag: vip", steps: 4, category: "retention" },
  { slug: "auto-event-followup", name: "Event / Webinar Follow-Up", desc: "Share recordings and drive next steps post-event.", trigger: "Tag: attended_event", steps: 5, category: "engagement" },
];

// Example flow for the visual demo
const DEMO_FLOW = [
  { type: "trigger", label: "New Subscriber Joins", icon: Zap, color: "bg-amber-50 text-amber-700 border-amber-200" },
  { type: "send", label: "Welcome Email", icon: Mail, color: "bg-coral-light text-coral border-coral/20" },
  { type: "delay", label: "Wait 1 Day", icon: Clock, color: "bg-blue-50 text-blue-700 border-blue-200" },
  { type: "send", label: "Value Email #2", icon: Mail, color: "bg-coral-light text-coral border-coral/20" },
  { type: "delay", label: "Wait 2 Days", icon: Clock, color: "bg-blue-50 text-blue-700 border-blue-200" },
  { type: "condition", label: "Did They Open?", icon: GitBranch, color: "bg-purple-50 text-purple-700 border-purple-200" },
  { type: "send", label: "Brand Story Email", icon: Mail, color: "bg-coral-light text-coral border-coral/20" },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "MoreMail Pre-Built Automation Flows",
  applicationCategory: "BusinessApplication",
  description: "12+ ready-to-install email automation workflows. Welcome series, abandoned cart recovery, re-engagement, and more. Just install and activate.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

export default function PrebuiltAutomationsLandingPage() {
  const [visibleCount, setVisibleCount] = useState(6);
  const visible = AUTOMATIONS.slice(0, visibleCount);

  return (
    <>
      <SeoHead
        title="Pre-Built Email Automations — Ready-to-Install Workflows | MoreMail"
        description="12+ expert-crafted email automation flows for welcome series, abandoned cart recovery, re-engagement & more. Install in one click, customize, and activate."
        canonical={`${SITE_URL}/prebuilt-automations`}
        keywords="email automation templates, prebuilt email workflows, welcome series automation, abandoned cart automation, email drip sequence templates, marketing automation templates, email workflow builder"
        ogTitle="12+ Pre-Built Email Automations — Install & Activate | MoreMail"
        ogDescription="Expert-crafted automation workflows with triggers, delays, conditions, and emails ready to go. Install in one click."
        jsonLd={JSON_LD}
      />

      {/* Hero */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-ink to-[#162029] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left — copy */}
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold mb-6 backdrop-blur-sm">
                <GitBranch size={13} className="text-coral" />
                {AUTOMATIONS.length} automation templates
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight mb-5">
                Email Automations<br />
                <span className="text-coral">Ready to Activate</span>
              </h1>
              <p className="text-white/60 text-lg max-w-lg mb-8 leading-relaxed">
                Pre-built workflows with triggers, delays, conditions, and email content.
                Install in one click, customize if you want, and activate. Your emails run on autopilot.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white px-7 py-3.5 rounded-lg text-sm font-bold transition-colors shadow-lg shadow-coral/20">
                  Start Free — Browse Templates <ArrowRight size={15} />
                </Link>
                <a href="#automations" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-7 py-3.5 rounded-lg text-sm font-semibold transition-colors backdrop-blur-sm">
                  <Play size={14} /> See All Templates
                </a>
              </div>
            </div>

            {/* Right — visual flow demo */}
            <div className="hidden lg:flex flex-col items-center">
              {DEMO_FLOW.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div key={i} className="flex flex-col items-center">
                    <div className={`w-64 rounded-xl px-4 py-3 flex items-center gap-3 border ${step.color}`}>
                      <div className="w-8 h-8 rounded-lg bg-white/60 flex items-center justify-center flex-shrink-0"><Icon size={15} /></div>
                      <span className="text-sm font-semibold">{step.label}</span>
                    </div>
                    {i < DEMO_FLOW.length - 1 && (
                      <div className="flex flex-col items-center py-0.5">
                        <div className="w-px h-2 bg-white/20" />
                        <ArrowDown size={12} className="text-white/30" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-b border-sand">
        <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: `${AUTOMATIONS.length}`, label: "Automation Templates", icon: GitBranch },
            { value: "9", label: "Trigger Types", icon: Zap },
            { value: "1-click", label: "Install Time", icon: Download },
            { value: "24/7", label: "Runs on Autopilot", icon: Clock },
          ].map((s) => (
            <div key={s.label}>
              <s.icon size={20} className="mx-auto text-coral mb-2" />
              <p className="font-display font-bold text-ink text-2xl">{s.value}</p>
              <p className="text-xs text-slate mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 bg-paper">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-ink text-center mb-10">
            Automation in <span className="text-coral">Three Clicks</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Pick a Workflow", desc: "Browse our library and choose an automation that fits your business.", icon: Target },
              { step: "2", title: "Install & Customize", desc: "One click installs it. Edit the emails, adjust timing, or use it as-is.", icon: Sparkles },
              { step: "3", title: "Activate & Relax", desc: "Flip it to active. It runs automatically in the background, 24/7.", icon: Zap },
            ].map((s) => (
              <div key={s.step} className="bg-white rounded-xl p-6 border border-sand text-center">
                <div className="w-10 h-10 bg-coral text-white rounded-full flex items-center justify-center font-bold text-sm mx-auto mb-4">{s.step}</div>
                <h3 className="font-display font-bold text-ink mb-2">{s.title}</h3>
                <p className="text-sm text-slate leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Automation library */}
      <section id="automations" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-ink text-center mb-3">
            Browse <span className="text-coral">Automation Templates</span>
          </h2>
          <p className="text-slate text-center text-sm mb-8 max-w-xl mx-auto">
            Every automation includes the trigger, email content, timing, and conditions — ready to install.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visible.map((a) => (
              <div key={a.slug} className="rounded-xl border border-sand bg-white p-5 hover:shadow-md hover:border-coral/30 transition-all">
                <div className="flex items-center gap-1.5 mb-2">
                  <Zap size={12} className="text-coral" />
                  <span className="text-[10px] font-semibold text-coral uppercase tracking-wide">{a.trigger}</span>
                </div>
                <h3 className="font-display font-bold text-ink text-base mb-1">{a.name}</h3>
                <p className="text-xs text-slate leading-relaxed mb-3">{a.desc}</p>
                <div className="flex items-center gap-4 text-[11px] text-slate">
                  <span className="flex items-center gap-1"><GitBranch size={11} /> {a.steps} steps</span>
                  <span className="text-[10px] text-slate/50 bg-paper px-1.5 py-0.5 rounded">{a.category}</span>
                </div>
              </div>
            ))}
          </div>

          {visible.length < AUTOMATIONS.length && (
            <div className="text-center mt-6">
              <button onClick={() => setVisibleCount(AUTOMATIONS.length)}
                className="inline-flex items-center gap-2 text-sm text-coral font-semibold hover:underline">
                Show all {AUTOMATIONS.length} automations <ArrowDown size={14} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* What's included */}
      <section className="py-16 bg-paper">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-ink text-center mb-10">Every Automation Includes</h2>
          <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {[
              "Smart trigger configuration (tags, events, inactivity)",
              "Timed delays between steps (minutes to days)",
              "Conditional branching based on subscriber behavior",
              "Full email content with subjects, body, and CTAs",
              "Personalization tokens ({{first_name}}, etc.)",
              "Webhook and multi-channel steps (LinkedIn, SMS)",
              "Installs as a draft — edit before activating",
              "Works with your existing subscriber lists",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 bg-white rounded-lg p-4 border border-sand">
                <Check size={16} className="text-teal shrink-0 mt-0.5" />
                <span className="text-sm text-ink">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-ink text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">Put Your Email Marketing on Autopilot</h2>
          <p className="text-white/60 text-base mb-8 leading-relaxed">
            {AUTOMATIONS.length} automation workflows ready to install. Welcome series, cart recovery, re-engagement, and more — running 24/7 without lifting a finger.
          </p>
          <Link to="/register" className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-8 py-4 rounded-lg font-bold transition-colors shadow-lg shadow-coral/20">
            Get Started Free <ArrowRight size={16} />
          </Link>
          <p className="text-white/30 text-xs mt-4">No credit card required • Free plan includes 1,000 subscribers</p>
        </div>
      </section>
    </>
  );
}
